import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebsiteLeads, LeadEntry, LeadType } from '../../contexts/WebsiteLeadsContext';
import { KTSVG } from '../../../_metronic/helpers';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const TYPE_CONFIG: Record<LeadType, {
  icon:        string;
  borderColor: string;
  label:       string;
}> = {
  enquiry:         { icon: 'bi-chat-left-text',    borderColor: 'var(--bs-primary)',  label: 'Enquiry'  },
  audit_pending:   { icon: 'bi-shop',              borderColor: 'var(--bs-warning)',  label: 'Audit'    },
  audit_submitted: { icon: 'bi-clipboard-check',   borderColor: 'var(--bs-info)',     label: 'Audit'    },
  audit_paid:      { icon: 'bi-shop-window',       borderColor: 'var(--bs-success)',  label: 'Audit'    },
  invoice_pending: { icon: 'bi-receipt',           borderColor: 'var(--bs-warning)',  label: 'Invoice'  },
  invoice_paid:    { icon: 'bi-receipt-cutoff',    borderColor: 'var(--bs-success)',  label: 'Invoice'  },
  subscription_submitted: { icon: 'bi-globe',   borderColor: 'var(--bs-warning)',  label: 'Subscription' },
  subscription_paid:      { icon: 'bi-globe2',  borderColor: 'var(--bs-success)',  label: 'Subscription' },
};

const STATUS_CONFIG: Partial<Record<LeadType, { label: string; color: string }>> = {
  audit_pending:   { label: 'Pending',  color: 'var(--bs-warning)' },
  audit_submitted: { label: 'Submitted', color: 'var(--bs-info)' },
  audit_paid:      { label: 'Paid',     color: 'var(--bs-success)' },
  invoice_pending: { label: 'Pending',  color: 'var(--bs-warning)' },
  invoice_paid:    { label: 'Received', color: 'var(--bs-success)' },
  subscription_submitted: { label: 'Lead',   color: 'var(--bs-warning)' },
  subscription_paid:      { label: 'Active', color: 'var(--bs-success)' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(date: Date): string {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function previewText(lead: LeadEntry): string {
  if (lead.type === 'enquiry') {
    return lead.subject || lead.message || lead.email;
  }
  if (lead.type === 'audit_pending' || lead.type === 'audit_submitted' || lead.type === 'audit_paid') {
    return lead.storeName || lead.storeUrl || lead.message || lead.email;
  }
  if (lead.type === 'invoice_pending' || lead.type === 'invoice_paid') {
    const amount = lead.amount !== undefined
      ? `$${lead.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`
      : '';
    return [lead.invoiceNumber, amount].filter(Boolean).join(' · ');
  }
  if (lead.type === 'subscription_submitted' || lead.type === 'subscription_paid') {
    const amount = lead.amount != null ? `$${lead.amount}/mo` : '';
    return [lead.storeName, amount, lead.testMode ? 'TEST' : ''].filter(Boolean).join(' · ') || lead.email;
  }
  return lead.email;
}

// ---------------------------------------------------------------------------
// Lead row (with expandable detail)
// ---------------------------------------------------------------------------

const LeadRow: React.FC<{
  lead:     LeadEntry;
  onClick:  (lead: LeadEntry) => void;
}> = ({ lead, onClick }) => {
  const cfg    = TYPE_CONFIG[lead.type];
  const status = STATUS_CONFIG[lead.type];
  const name   = [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.email;
  const preview = previewText(lead);

  return (
    <div
      className="wl-row"
      style={{ borderLeft: `3px solid ${cfg.borderColor}` }}
      onClick={() => onClick(lead)}
    >
      {/* Icon */}
      <div className="wl-row-icon">
        <i className={`bi ${cfg.icon}`} style={{ color: cfg.borderColor, fontSize: 16 }}></i>
      </div>

      {/* Content */}
      <div className="wl-row-body">
        <div className="wl-row-top">
          <span className="wl-row-name">{name}</span>
          <span className="wl-row-time">{timeAgo(lead.createdAt)}</span>
        </div>
        <div className="wl-row-preview">
          {preview}
          {status && (
            <span className="wl-status-dot" style={{ background: status.color }} title={status.label}></span>
          )}
        </div>
        {lead.email && name !== lead.email && (
          <div className="wl-row-email">{lead.email}</div>
        )}
      </div>

      {/* Arrow */}
      <i className="bi bi-chevron-right wl-chevron"></i>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

const WebsiteLeadsPanel: React.FC = () => {
  const { leads, loading, isPanelOpen, closeLeadsPanel } = useWebsiteLeads();
  const navigate = useNavigate();

  // Mount / unmount with animation — same pattern as EmailSlidingPanel
  const [shouldRender,    setShouldRender]    = useState(false);
  const [isActuallyOpen,  setIsActuallyOpen]  = useState(false);

  useEffect(() => {
    if (isPanelOpen) {
      setShouldRender(true);
      const t = setTimeout(() => setIsActuallyOpen(true), 10);
      return () => clearTimeout(t);
    } else {
      setIsActuallyOpen(false);
      const t = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(t);
    }
  }, [isPanelOpen]);

  const handleRowClick = (lead: LeadEntry) => {
    closeLeadsPanel();
    navigate('/website-submissions', { state: { openLeadId: `${lead.collection}::${lead.id}` } });
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* ── Styles ── */}
      <style>{`
        .wl-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          z-index: 1040;
          transition: opacity 0.35s ease;
        }
        .wl-backdrop.closed { opacity: 0; pointer-events: none; }
        .wl-backdrop.open   { opacity: 1; }

        .wl-panel {
          position: fixed;
          top: 0; right: 0;
          height: 100vh;
          width: min(400px, 100vw);
          background: var(--bs-body-bg);
          box-shadow: -4px 0 24px rgba(0,0,0,0.18);
          z-index: 1050;
          display: flex;
          flex-direction: column;
          transition: transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease;
          will-change: transform, opacity;
        }
        .wl-panel.closed {
          transform: translateX(100%);
          opacity: 0;
          pointer-events: none;
        }
        .wl-panel.open {
          transform: translateX(0);
          opacity: 1;
        }

        /* Header */
        .wl-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          border-bottom: 1px solid var(--bs-border-color);
          background: var(--bs-body-secondary, var(--bs-card-bg));
          flex-shrink: 0;
        }
        .wl-header-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--bs-body-color);
          margin: 0;
        }
        .wl-header-sub {
          font-size: 12px;
          color: var(--bs-gray-500, #a1a5b7);
          margin-top: 1px;
        }

        /* Feed */
        .wl-feed {
          flex: 1;
          overflow-y: auto;
        }

        /* Row */
        .wl-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px 12px 14px;
          border-bottom: 1px solid var(--bs-border-color);
          cursor: default;
          position: relative;
          transition: background 0.15s ease;
          min-height: 44px;
        }
        .wl-row {
          cursor: pointer;
        }
        .wl-row:hover {
          background: rgba(var(--bs-primary-rgb), 0.05);
        }
        .wl-row--expanded {
          background: rgba(var(--bs-primary-rgb), 0.04);
          align-items: flex-start;
        }
        .wl-row-icon {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(var(--bs-primary-rgb), 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .wl-row-body {
          flex: 1;
          min-width: 0;
        }
        .wl-row-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .wl-row-name {
          font-size: 13px;
          font-weight: 600;
          color: var(--bs-body-color);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .wl-row-time {
          font-size: 11px;
          color: var(--bs-gray-500, #a1a5b7);
          flex-shrink: 0;
        }
        .wl-row-preview {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--bs-gray-600, #7e8299);
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .wl-row-email {
          font-size: 11px;
          color: var(--bs-gray-500, #a1a5b7);
          margin-top: 1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .wl-status-dot {
          display: inline-block;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        /* Chevron */
        .wl-chevron {
          flex-shrink: 0;
          font-size: 11px;
          color: var(--bs-gray-500, #a1a5b7);
          transition: color 0.15s ease;
        }
        .wl-row:hover .wl-chevron { color: var(--bs-body-color); }

        /* Expanded detail */
        .wl-detail {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid var(--bs-border-color);
        }
        .wl-detail-row {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 12px;
          color: var(--bs-body-color);
          margin-bottom: 5px;
        }
        .wl-detail-icon {
          font-size: 12px;
          color: var(--bs-gray-500, #a1a5b7);
          flex-shrink: 0;
          width: 14px;
          text-align: center;
        }
        .wl-detail-link {
          color: var(--bs-primary);
          text-decoration: none;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .wl-detail-link:hover { text-decoration: underline; }
        .wl-detail-amount {
          font-weight: 600;
          color: var(--bs-success);
        }
        .wl-detail-message {
          font-size: 12px;
          color: var(--bs-gray-600, #7e8299);
          margin: 6px 0;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .wl-detail-delete {
          display: inline-flex;
          align-items: center;
          margin-top: 8px;
          padding: 4px 10px;
          border: 1px solid rgba(var(--bs-danger-rgb), 0.3);
          border-radius: 6px;
          background: transparent;
          color: var(--bs-danger);
          font-size: 11px;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .wl-detail-delete:hover {
          background: rgba(var(--bs-danger-rgb), 0.1);
        }

        /* Footer */
        .wl-footer {
          padding: 10px 20px;
          border-top: 1px solid var(--bs-border-color);
          font-size: 11px;
          color: var(--bs-gray-500, #a1a5b7);
          text-align: center;
          flex-shrink: 0;
        }

        /* Empty state */
        .wl-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 70%;
          padding: 40px 24px;
          text-align: center;
          color: var(--bs-gray-500, #a1a5b7);
        }
        .wl-empty-icon {
          font-size: 40px;
          margin-bottom: 16px;
          opacity: 0.4;
        }
        .wl-empty-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--bs-body-color);
          margin-bottom: 6px;
        }
        .wl-empty-sub {
          font-size: 13px;
        }

        /* Skeleton */
        .wl-skeleton-row {
          padding: 14px 20px;
          border-bottom: 1px solid var(--bs-border-color);
        }
      `}</style>

      {/* ── Backdrop ── */}
      <div
        className={`wl-backdrop${isActuallyOpen ? ' open' : ' closed'}`}
        onClick={closeLeadsPanel}
      />

      {/* ── Panel ── */}
      <div className={`wl-panel${isActuallyOpen ? ' open' : ' closed'}`}>

        {/* Header */}
        <div className="wl-header">
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-broadcast" style={{ fontSize: 20, color: 'var(--bs-primary)' }}></i>
            <div>
              <div className="wl-header-title">Website Activity</div>
              <div className="wl-header-sub">
                {loading
                  ? 'Connecting…'
                  : `${leads.length} record${leads.length !== 1 ? 's' : ''} · live`
                }
              </div>
            </div>
          </div>
          <button
            className="btn btn-icon btn-sm btn-active-light-primary"
            onClick={closeLeadsPanel}
            aria-label="Close panel"
          >
            <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-2" />
          </button>
        </div>

        {/* Feed */}
        <div className="wl-feed">

          {/* Loading skeleton */}
          {loading && [1, 2, 3, 4].map((i) => (
            <div key={i} className="wl-skeleton-row placeholder-glow">
              <span className="placeholder col-5 rounded mb-2 d-block" style={{ height: 12 }}></span>
              <span className="placeholder col-8 rounded d-block" style={{ height: 10 }}></span>
            </div>
          ))}

          {/* Empty state */}
          {!loading && leads.length === 0 && (
            <div className="wl-empty">
              <i className="bi bi-broadcast wl-empty-icon"></i>
              <div className="wl-empty-title">No activity yet</div>
              <div className="wl-empty-sub">
                Leads and payments from the website will appear here in real time.
              </div>
            </div>
          )}

          {/* Rows */}
          {!loading && leads.map((lead) => (
            <LeadRow
              key={`${lead.collection}-${lead.id}`}
              lead={lead}
              onClick={handleRowClick}
            />
          ))}
        </div>

        {/* Footer */}
        {!loading && leads.length > 0 && (
          <div className="wl-footer">
            {leads.length} record{leads.length !== 1 ? 's' : ''} · updates in real time
          </div>
        )}
      </div>
    </>
  );
};

export default WebsiteLeadsPanel;
